import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    FileText,
    FileImage,
    File as FileIcon,
    Trash2,
    Loader2,
    Download,
    ExternalLink,
    Pencil,
    Archive,
    ArchiveRestore,
    Link as LinkIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API, tokenStore } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRevalidateOnFocus } from "../lib/useRevalidateOnFocus";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import EditChangeDocumentDialog from "./EditChangeDocumentDialog";
import FlagButton from "./FlagButton";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * Iter-62 PART 9 (rev iter-78): Read-only chronological timeline of
 * uploaded note documents — supervisor notes, reference docs,
 * cast/scene notes from set. Reference-only; never alters scenes,
 * characters or script structure. Filterable by scene/character.
 * Owners can delete a document; everyone can download/preview.
 */
function _formatRelative(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const diffMs = Date.now() - d.getTime();
        const min = Math.round(diffMs / 60000);
        if (min < 1) return "just now";
        if (min < 60) return `${min}m ago`;
        const hr = Math.round(min / 60);
        if (hr < 24) return `${hr}h ago`;
        const day = Math.round(hr / 24);
        if (day < 30) return `${day}d ago`;
        return d.toLocaleDateString();
    } catch {
        return iso;
    }
}

// Display labels for note documents. Legacy `coloured_pages` /
// `scene_pages` rows uploaded under earlier builds (when those kinds
// were exposed) collapse to a neutral "Document" label so Notes UI
// never echoes Revision-specific language.
const KIND_LABEL = {
    supervisor_notes: "Supervisor",
    scene_notes: "Scene note",
    cast_notes: "Cast note",
    other: "Other",
    coloured_pages: "Document",
    scene_pages: "Document",
    // Iter-131b — category kinds (mirror DocumentViewerPage prettyKind).
    call_sheet: "Call sheet",
    schedule: "Schedule",
    email: "Email",
    reference: "Reference",
    budget: "Budget",
    location: "Location",
    continuity: "Continuity",
    research: "Research",
    link: "Link",
};

function _iconFor(mime) {
    const m = (mime || "").toLowerCase();
    if (m.startsWith("image/")) return FileImage;
    if (m.includes("pdf") || m.includes("word") || m.includes("text")) return FileText;
    return FileIcon;
}

export default function ChangeDocumentsTimeline({
    projectId,
    sceneId,
    characterId,
    isOwner = false,
    testidPrefix = "change-documents",
    emptyHint = "No uploaded documents yet.",
    onChange,
    // iter-101 — optional server-side episode filter.
    episode = null,
    // Iter-131b — new filters surfaced from the parent page.
    archived = "false", // "false" | "true" | "all"
    kinds = null, // null | comma-separated string of kind values
}) {
    const { user } = useAuth();
    const currentUserId = user?.id || null;
    const routeBase = useRouteBase();
    const apiBase = useApiBase();
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [deletingId, setDeletingId] = useState(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [archivingId, setArchivingId] = useState(null);

    // Iter-134 — snapshot the current pathname + search + filter
    // state so the DocumentViewer's Close button can return the
    // user EXACTLY where they came from (preserves episode + scene
    // + character + archive filters on Notes/Documents).
    const location = useLocation();
    const returnFrom = {
        pathname: location.pathname,
        search: location.search,
        state: location.state,
    };

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (sceneId) params.set("scene_id", sceneId);
            if (characterId) params.set("character_id", characterId);
            if (episode) params.set("episode", episode);
            if (archived && archived !== "false") params.set("archived", archived);
            if (kinds) params.set("kind", kinds);
            const qs = params.toString();
            const { data } = await api.get(
                `${apiBase}/${projectId}/change-documents${qs ? `?${qs}` : ""}`,
            );
            setDocs(data || []);
            setError("");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [projectId, sceneId, characterId, episode, archived, kinds, apiBase]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Iter-156E — re-fetch the documents timeline on tab focus so
    // out-of-band uploads / link additions / archive toggles by
    // teammates appear without a manual refresh.
    useRevalidateOnFocus(refresh);

    const onDelete = async (id) => {
        setDeletingId(id);
        try {
            await api.delete(`${apiBase}/${projectId}/change-documents/${id}`);
            setDocs((prev) => prev.filter((d) => d.id !== id));
            toast.success("Document removed");
            if (onChange) onChange();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setDeletingId(null);
            setConfirmDeleteId(null);
        }
    };
    const requestDelete = (id) => setConfirmDeleteId(id);

    // Iter-97 PART 6 — owner+admin can edit document metadata.
    const [editingDoc, setEditingDoc] = useState(null);
    const [editScenes, setEditScenes] = useState([]);
    const [editChars, setEditChars] = useState([]);
    const startEdit = async (doc) => {
        if (editScenes.length === 0 || editChars.length === 0) {
            try {
                const [{ data: sc }, { data: ch }] = await Promise.all([
                    api.get(`${apiBase}/${projectId}/scenes`),
                    api.get(`${apiBase}/${projectId}/characters`),
                ]);
                setEditScenes(sc || []);
                setEditChars(ch || []);
            } catch {
                /* dialog renders with empty selects */
            }
        }
        setEditingDoc(doc);
    };
    const saveEdit = async (payload) => {
        const { data } = await api.patch(
            `${apiBase}/${projectId}/change-documents/${editingDoc.id}`,
            payload,
        );
        setDocs((prev) => prev.map((d) => (d.id === data.id ? data : d)));
        toast.success("Document updated");
        if (onChange) onChange();
    };

    const onToggleArchive = async (d) => {
        setArchivingId(d.id);
        try {
            const next = !d.archived;
            const { data } = await api.patch(
                `${apiBase}/${projectId}/change-documents/${d.id}`,
                { archived: next },
            );
            setDocs((prev) => prev.map((x) => (x.id === d.id ? data : x)));
            toast.success(next ? "Document archived" : "Document restored");
            if (onChange) onChange();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setArchivingId(null);
        }
    };

    const fileUrl = (id, download = false) => {
        const token = tokenStore.get() || "";
        const qs = new URLSearchParams();
        if (token) qs.set("token", token);
        if (download) qs.set("download", "1");
        return `${API}${apiBase}/${projectId}/change-documents/${id}/file?${qs.toString()}`;
    };

    if (loading) {
        return (
            <div
                data-testid={`${testidPrefix}-loading`}
                className="h-16 rounded-xl border border-white/10 bg-card/40 animate-pulse"
            />
        );
    }
    if (error) {
        return (
            <p
                data-testid={`${testidPrefix}-error`}
                className="text-xs text-red-300"
            >
                {error}
            </p>
        );
    }
    if (docs.length === 0) {
        return (
            <p
                data-testid={`${testidPrefix}-empty`}
                className="text-[11px] text-neutral-500 italic"
            >
                {emptyHint}
            </p>
        );
    }

    return (
        <>
        <ul data-testid={`${testidPrefix}-list`} className="space-y-2">
            {docs.map((d) => {
                const Icon = _iconFor(d.mime_type);
                return (
                    <li
                        key={d.id}
                        data-testid={`${testidPrefix}-row-${d.id}`}
                        className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-3"
                    >
                        <div className="flex items-start gap-3">
                            <div className="shrink-0 rounded-md bg-violet-500/15 border border-violet-500/30 h-10 w-10 flex items-center justify-center mt-0.5">
                                <Icon size={18} className="text-violet-200" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 mb-0.5">
                                    <span
                                        data-testid={`${testidPrefix}-doc-pill-${d.id}`}
                                        className="text-[9px] uppercase tracking-[0.2em] text-violet-200 bg-violet-500/15 border border-violet-500/30 rounded-full px-1.5 py-0.5 font-semibold"
                                    >
                                        Document
                                    </span>
                                    <span className="text-[9px] uppercase tracking-[0.2em] text-violet-200 bg-violet-500/10 border border-violet-500/20 rounded-full px-1.5 py-0.5">
                                        {KIND_LABEL[d.kind] || d.kind}
                                    </span>
                                    {d.archived ? (
                                        <span
                                            data-testid={`${testidPrefix}-archived-pill-${d.id}`}
                                            className="text-[9px] uppercase tracking-[0.2em] text-amber-200 bg-amber-500/15 border border-amber-500/30 rounded-full px-1.5 py-0.5 font-semibold"
                                        >
                                            Archived
                                        </span>
                                    ) : null}
                                    {d.link_url || d.kind === "link" ? (
                                        <span
                                            data-testid={`${testidPrefix}-link-pill-${d.id}`}
                                            className="text-[9px] uppercase tracking-[0.2em] text-emerald-200 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-1.5 py-0.5 font-semibold"
                                        >
                                            External
                                        </span>
                                    ) : null}
                                </div>
                                <p
                                    className="text-sm text-white font-semibold truncate"
                                    data-testid={`${testidPrefix}-label-${d.id}`}
                                >
                                    {d.label || d.filename || "Untitled change"}
                                </p>
                                <p
                                    data-testid={`${testidPrefix}-filename-${d.id}`}
                                    className="text-[11px] text-neutral-300 truncate mt-0.5 mono"
                                >
                                    {d.filename}
                                    {d.size
                                        ? ` · ${(d.size / 1024).toFixed(1)} KB`
                                        : ""}
                                </p>
                                <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                                    {d.scene_number ? (
                                        d.scene_id ? (
                                            <Link
                                                to={`${routeBase}/${projectId}/scenes/${d.scene_id}`}
                                                data-testid={`${testidPrefix}-scene-ref-${d.id}`}
                                                className="text-[9px] uppercase tracking-[0.2em] text-violet-200 bg-violet-500/15 border border-violet-500/30 rounded-full px-1.5 py-0.5 hover:bg-violet-500/25"
                                            >
                                                Sc {d.scene_number}
                                            </Link>
                                        ) : (
                                            <span
                                                data-testid={`${testidPrefix}-scene-ref-${d.id}`}
                                                className="text-[9px] uppercase tracking-[0.2em] text-violet-200 bg-violet-500/15 border border-violet-500/30 rounded-full px-1.5 py-0.5"
                                            >
                                                Sc {d.scene_number}
                                            </span>
                                        )
                                    ) : null}
                                    {d.character_name ? (
                                        d.character_id ? (
                                            <Link
                                                to={`${routeBase}/${projectId}/characters/${d.character_id}`}
                                                data-testid={`${testidPrefix}-char-ref-${d.id}`}
                                                className="text-[9px] uppercase tracking-[0.2em] text-violet-200 bg-violet-500/15 border border-violet-500/30 rounded-full px-1.5 py-0.5 hover:bg-violet-500/25"
                                            >
                                                {d.character_name}
                                            </Link>
                                        ) : (
                                            <span
                                                data-testid={`${testidPrefix}-char-ref-${d.id}`}
                                                className="text-[9px] uppercase tracking-[0.2em] text-violet-200 bg-violet-500/15 border border-violet-500/30 rounded-full px-1.5 py-0.5"
                                            >
                                                {d.character_name}
                                            </span>
                                        )
                                    ) : null}
                                    <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-400">
                                        {_formatRelative(d.uploaded_at)}
                                        {d.uploaded_by_name
                                            ? ` · ${d.uploaded_by_name}`
                                            : ""}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                            {d.kind === "link" || d.link_url ? (
                                <a
                                    href={d.link_url || "#"}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    data-testid={`${testidPrefix}-open-external-${d.id}`}
                                    className="tap flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-500/85 hover:bg-emerald-500 text-white px-3 h-9 text-[12px] font-semibold"
                                >
                                    <LinkIcon size={12} />
                                    Open external link
                                </a>
                            ) : (
                                <Link
                                    to={`${routeBase}/${projectId}/documents/${d.id}`}
                                    // Iter-134 — preserve return surface
                                    // (Notes/Documents incl. filter state)
                                    // so the user lands back where they
                                    // launched the viewer.
                                    state={{ from: returnFrom }}
                                    data-testid={`${testidPrefix}-open-${d.id}`}
                                    className="tap flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 h-9 text-[12px] font-semibold"
                                >
                                    <ExternalLink size={12} />
                                    View document
                                </Link>
                            )}
                            {!d.link_url && d.kind !== "link" ? (
                                <a
                                    href={fileUrl(d.id, true)}
                                    data-testid={`${testidPrefix}-download-${d.id}`}
                                    title="Download"
                                    aria-label="Download document"
                                    className="tap inline-flex items-center justify-center rounded-md bg-secondary border border-white/10 text-neutral-200 hover:bg-white/10 h-9 w-9"
                                >
                                    <Download size={14} />
                                </a>
                            ) : null}
                            <FlagButton
                                projectId={projectId}
                                entityType={d.kind === "continuity" ? "continuity_note" : "document"}
                                entityId={d.id}
                                entityLabel={d.label || "document"}
                                size="lg"
                                testid={`${testidPrefix}-flag-${d.id}`}
                            />
                            {/* Iter-156G — Archive (governance) stays
                                Admin-only; Edit/Delete are creator-or-admin
                                to mirror the supervisor_notes ACL. */}
                            {isOwner ? (
                                <button
                                    type="button"
                                    data-testid={`${testidPrefix}-archive-${d.id}`}
                                    onClick={() => onToggleArchive(d)}
                                    disabled={archivingId === d.id}
                                    aria-label={d.archived ? "Restore from archive" : "Archive document"}
                                    title={d.archived ? "Restore" : "Archive (non-destructive)"}
                                    className={`tap inline-flex items-center justify-center rounded-md h-9 w-9 ${
                                        d.archived
                                            ? "text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10"
                                            : "text-neutral-400 hover:text-amber-300 hover:bg-amber-500/10"
                                    }`}
                                >
                                    {archivingId === d.id ? (
                                        <Loader2 size={13} className="animate-spin" />
                                    ) : d.archived ? (
                                        <ArchiveRestore size={13} />
                                    ) : (
                                        <Archive size={13} />
                                    )}
                                </button>
                            ) : null}
                            {(isOwner || (currentUserId && d.uploaded_by === currentUserId)) ? (
                                <>
                                    <button
                                        type="button"
                                        data-testid={`${testidPrefix}-edit-${d.id}`}
                                        onClick={() => startEdit(d)}
                                        aria-label="Edit document metadata"
                                        title="Edit"
                                        className="tap inline-flex items-center justify-center rounded-md h-9 w-9 text-neutral-400 hover:text-violet-200 hover:bg-violet-500/10"
                                    >
                                        <Pencil size={13} />
                                    </button>
                                    <button
                                        type="button"
                                        data-testid={`${testidPrefix}-delete-${d.id}`}
                                        onClick={() => requestDelete(d.id)}
                                        disabled={deletingId === d.id}
                                        aria-label="Delete note document"
                                        className="tap inline-flex items-center justify-center rounded-md h-9 w-9 text-neutral-400 hover:text-red-300 hover:bg-red-500/10"
                                    >
                                        {deletingId === d.id ? (
                                            <Loader2
                                                size={12}
                                                className="animate-spin"
                                            />
                                        ) : (
                                            <Trash2 size={12} />
                                        )}
                                    </button>
                                </>
                            ) : null}
                        </div>
                    </li>
                );
            })}
        </ul>
        {/* iter-73 PART 3 — destructive-action confirmation. */}
        <AlertDialog
            open={Boolean(confirmDeleteId)}
            onOpenChange={(o) => !o && setConfirmDeleteId(null)}
        >
            <AlertDialogContent data-testid={`${testidPrefix}-delete-confirm`}>
                <AlertDialogHeader>
                    <AlertDialogTitle>Delete this note?</AlertDialogTitle>
                    <AlertDialogDescription>
                        This action cannot be undone.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel
                        data-testid={`${testidPrefix}-delete-confirm-cancel`}
                    >
                        Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                        data-testid={`${testidPrefix}-delete-confirm-delete`}
                        onClick={() =>
                            confirmDeleteId && onDelete(confirmDeleteId)
                        }
                        className="bg-red-600 text-white hover:bg-red-500"
                    >
                        Delete
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
        <EditChangeDocumentDialog
            open={Boolean(editingDoc)}
            onOpenChange={(v) => !v && setEditingDoc(null)}
            document={editingDoc}
            scenes={editScenes}
            characters={editChars}
            onSaved={saveEdit}
        />
        </>
    );
}
